import { useRouter } from 'next/router';
import useLocale from 'next-translate/useTranslation';
import { useCallback, useState } from 'react';

import clsxm from '@/lib/clsxm';

import { useDashboardTrack } from '@/components/dashboard/ui';

import { useUser } from '@/utils/useUser';

import type { NavBadgeKind } from './navModel';
import PinIcon from './PinIcon';

/**
 * Colour roles of the app navigation, on the dashboard kit's palette
 * (`dashboard/ui.tsx`): a white panel with a hairline, rows in the body grey,
 * the current page in an indigo tint like the phone tab bar's active tab.
 */
export const nav = {
  panel: 'bg-white dark:bg-slate-900',
  hairline: 'border-[#0f0f28]/[0.07] dark:border-white/[0.08]',
  ink: 'text-[#10101c] dark:text-slate-100',
  body: 'text-[#56566d] dark:text-slate-300',
  quiet: 'text-[#6f6f86] dark:text-slate-400',
  soft: 'bg-[#f4f4fb] dark:bg-white/[0.05]',
  hover:
    'hover:bg-[#f4f4fb] hover:text-[#10101c] dark:hover:bg-white/[0.06] dark:hover:text-white',
  active:
    'bg-primary-50 text-primary-700 dark:bg-primary-500/15 dark:text-primary-200',
  icon: 'text-[#8b8ba0] group-hover:text-[#56566d] dark:text-slate-500 dark:group-hover:text-slate-300',
  iconActive: 'text-primary-600 dark:text-primary-300',
  /** Inside the row, so a scroll container's edge never clips it. */
  focus:
    'focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-primary-600',
};

export type NavVariant = 'sidebar' | 'rail' | 'drawer';

/**
 * Click tracking for the sidebar, the narrow rail and the phone drawer.
 * PostHog autocapture is off, and these links go to the same pages as the
 * dashboard, the tab bar and in-page links, so pageviews can't tell them apart.
 */
export function useNavTrack(variant: NavVariant) {
  const track = useDashboardTrack('Nav Click');
  const { pathname } = useRouter();
  return useCallback(
    (section: string, target: string, extra?: Record<string, unknown>) =>
      track(section, target, { variant, from: pathname, ...extra }),
    [track, variant, pathname],
  );
}

const BADGE_STYLES: Record<NavBadgeKind, string> = {
  new: 'bg-primary-50 text-primary-700 dark:bg-primary-500/15 dark:text-primary-200',
  beta: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300',
};

const BADGE_LABELS: Record<NavBadgeKind, string> = {
  new: 'New',
  beta: 'Beta',
};

export function NavItemBadge({ kind }: { kind: NavBadgeKind }) {
  const { t } = useLocale('dashboard');
  return (
    <span
      className={clsxm(
        'shrink-0 rounded-full px-1.5 py-px text-[10px] font-semibold leading-4',
        BADGE_STYLES[kind],
      )}
    >
      {t(BADGE_LABELS[kind])}
    </span>
  );
}

/**
 * Pushpin that pins a tool to the sidebar or takes it off: solid when pinned,
 * an outline when not (a star until 2026-10-23; /presets and the tool page
 * still show stars for the same favorites list). It sits next to the link,
 * never inside it: a button inside a link is invalid and gave keyboard users
 * two stops per row.
 */
export function PinButton({
  pinned,
  onToggle,
  className,
  reveal = 'hover',
}: {
  pinned: boolean;
  onToggle: () => void;
  className?: string;
  /**
   * 'hover': takes no room until its row (`group/row`) is hovered or
   * focused, so a label isn't cut short for it (sidebar rows). 'pinned':
   * always shown once pinned, as in the Tools flyout. A hidden pin takes no
   * taps, so a touch screen can't unpin by accident.
   */
  reveal?: 'hover' | 'pinned';
}) {
  const { t } = useLocale('dashboard');
  const label = pinned ? t('Unpin from sidebar') : t('Pin to sidebar');
  return (
    <button
      type='button'
      onClick={onToggle}
      aria-label={label}
      aria-pressed={pinned}
      title={label}
      className={clsxm(
        'flex h-6 shrink-0 items-center justify-center overflow-hidden rounded-md transition-opacity',
        'hover:bg-[#0f0f28]/[0.06] dark:hover:bg-white/10',
        'focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary-600',
        reveal === 'hover'
          ? 'pointer-events-none w-0 opacity-0 focus-visible:me-1 focus-visible:w-6 focus-visible:opacity-100 group-hover/row:pointer-events-auto group-hover/row:me-1 group-hover/row:w-6 group-hover/row:opacity-100 group-focus-within/row:me-1 group-focus-within/row:w-6 group-focus-within/row:opacity-100'
          : clsxm(
              'w-6',
              !pinned &&
                'pointer-events-none opacity-0 focus-visible:opacity-100 group-hover/row:pointer-events-auto group-hover/row:opacity-100 group-focus-within/row:opacity-100',
            ),
        className,
      )}
    >
      <PinIcon
        solid={pinned}
        className={clsxm(
          'h-3.5 w-3.5 transition-colors',
          pinned
            ? 'text-[#56566d] hover:text-[#10101c] dark:text-slate-300 dark:hover:text-white'
            : 'text-[#8b8ba0] hover:text-[#56566d] dark:text-slate-500 dark:hover:text-slate-300',
        )}
      />
    </button>
  );
}

/** Initials, or the profile photo when there is one. */
export function Avatar({ size = 28 }: { size?: number }) {
  const { user, userDetails } = useUser();
  const [failed, setFailed] = useState(false);
  const photo =
    userDetails?.avatar_url ||
    (user?.user_metadata?.avatar_url as string | undefined) ||
    (user?.user_metadata?.picture as string | undefined);
  const name = displayName(userDetails, user?.email);
  const initials =
    name
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0])
      .join('')
      .toUpperCase() || '?';

  return (
    <span
      aria-hidden='true'
      style={{ width: size, height: size }}
      className='flex shrink-0 items-center justify-center overflow-hidden rounded-full bg-primary-100 text-[11px] font-semibold text-primary-700 ring-1 ring-[#0f0f28]/[0.06] dark:bg-primary-500/20 dark:text-primary-200 dark:ring-white/10'
    >
      {photo && !failed ? (
        // eslint-disable-next-line @next/next/no-img-element
        <img
          src={photo}
          alt=''
          referrerPolicy='no-referrer'
          onError={() => setFailed(true)}
          className='h-full w-full object-cover'
        />
      ) : (
        initials
      )}
    </span>
  );
}

export function displayName(
  details: { first_name?: string; last_name?: string } | null | undefined,
  email?: string | null,
) {
  const full = [details?.first_name, details?.last_name]
    .filter(Boolean)
    .join(' ')
    .trim();
  if (full) return full;
  return email ? email.split('@')[0] : '';
}
